import { AxiosPromise } from "axios";
import { CookieAuthorizedApiWithAuthRefresh, BaseApi } from "ecom-teams-apps-core";
import { TaskDto } from "../service/api/TaskDto";
import { format } from "date-fns";
import { DateRange } from "shared/models/DateRange";
import { constants } from "shared/constants";
import { DictionaryItemDto } from "tasksBoard/service/api/DictionaryItemDto";
import { UserIdentityDto } from "tasksBoard/service/api/UserIdentityDto";
import { SystemFileDto } from "./api/SystemFileDto";
import { TokenDto } from "./api/TokenDto";
import AppErrorDto from "./api/AppErrorDto";
import refreshAuthMethodProvider from "authorization/RefreshAuthMethodProvider";

export interface UpdateTaskStatusData {
    issueId: number;
    previousId: number | null;
    status: {
        code: string;
    };
}

export interface TasksService {
    getTasks(range: DateRange): AxiosPromise<TaskDto[]>;
    getTask(id: number): AxiosPromise<TaskDto>;
    updateTaskStatus: (updateTaskStatusData: UpdateTaskStatusData) => AxiosPromise<AppErrorDto | undefined>;
    updateTaskAttribute: (taskId: number, attributeId: number, customValue: DictionaryItemDto) => AxiosPromise;
    updateTaskAttributeMulti: (
        taskId: number,
        attributeId: number,
        customValues: DictionaryItemDto[]
    ) => AxiosPromise<TaskDto>;
    addComment: (taskId: number, content: string) => AxiosPromise;
    updateComment: (taskId: number, commentId: number, content: string) => AxiosPromise;
    deleteComment: (taskId: number, commentId: number) => AxiosPromise;
    updatePlannedDate: (taskId: number, date: Date) => AxiosPromise;
    assignUser: (taskId: number, userName: string) => AxiosPromise;
    getAvailableAssignees: (taskId: number) => AxiosPromise<UserIdentityDto[]>;
    uploadFile: (file: File) => AxiosPromise<SystemFileDto>;
    addAttachment: (taskId: number, attachmentHash: string) => AxiosPromise;
    removeAttachment: (taskId: number, attachmentId: number) => AxiosPromise<TaskDto>;
    getFileToken: (attachmentHash: string) => AxiosPromise<TokenDto>;
    retrieveFile: (token: string) => AxiosPromise<Blob>;
}

export class DefaultTasksService implements TasksService {
    private getTasksUrl: string =
        "/secured/dashboard?attributeList=TAG_LIST,CONTACT_TYPE," + constants.attributeCodes.sellerInformation;
    private dashboardUrl: string = "/secured/dashboard";
    private updateTaskStatusUrl: string = "/secured/card";
    private filesUrl: string = "/secured/file";

    constructor(private cookieAuthorizedApi: BaseApi) {}

    getTask(id: number): AxiosPromise<TaskDto> {
        return this.cookieAuthorizedApi.get<TaskDto>(`${this.dashboardUrl}/${id}`);
    }

    getTasks(range: DateRange): AxiosPromise<TaskDto[]> {
        return this.cookieAuthorizedApi.get<TaskDto[]>(
            `${this.getTasksUrl}&startDate=${this.formatDate(range.start)}&endDate=${this.formatDate(range.end)}`
        );
    }

    updateTaskStatus(updateTaskStatusData: UpdateTaskStatusData): AxiosPromise<AppErrorDto | undefined> {
        return this.cookieAuthorizedApi.put(this.updateTaskStatusUrl, updateTaskStatusData);
    }

    updateTaskAttribute(taskId: number, attributeId: number, customValue: DictionaryItemDto): AxiosPromise {
        return this.cookieAuthorizedApi.put(`${this.dashboardUrl}/${taskId}/attribute/${attributeId}`, {
            id: attributeId,
            customValue: {
                className: "io.lsn.pinpoint.domain.dashboard.entity.attribute.DictionaryItemAttribute",
                value: customValue,
            },
        });
    }

    updateTaskAttributeMulti(
        taskId: number,
        attributeId: number,
        customValues: DictionaryItemDto[]
    ): AxiosPromise<TaskDto> {
        return this.cookieAuthorizedApi.put(`${this.dashboardUrl}/${taskId}/attribute/${attributeId}`, {
            id: attributeId,
            customValue: {
                className: "io.lsn.pinpoint.domain.dashboard.entity.attribute.DictionaryItemListAttribute",
                value: customValues,
            },
        });
    }

    addComment(taskId: number, content: string): AxiosPromise {
        return this.cookieAuthorizedApi.post(`${this.dashboardUrl}/${taskId}/comment`, { body: content });
    }

    updateComment(taskId: number, commentId: number, content: string): AxiosPromise {
        return this.cookieAuthorizedApi.put(`${this.dashboardUrl}/${taskId}/comment/${commentId}`, { body: content });
    }

    deleteComment(taskId: number, commentId: number): AxiosPromise {
        return this.cookieAuthorizedApi.delete(`${this.dashboardUrl}/${taskId}/comment/${commentId}`);
    }

    updatePlannedDate(taskId: number, date: Date): AxiosPromise {
        return this.cookieAuthorizedApi.put(`${this.dashboardUrl}/${taskId}/planned-date`, {
            plannedDate: this.formatDate(date),
        });
    }

    assignUser(taskId: number, userName: string): AxiosPromise {
        return this.cookieAuthorizedApi.put(`${this.dashboardUrl}/${taskId}/assignee`, {
            assignee: { username: userName },
        });
    }

    getAvailableAssignees(taskId: number): AxiosPromise<UserIdentityDto[]> {
        return this.cookieAuthorizedApi.get<UserIdentityDto[]>(`${this.dashboardUrl}/${taskId}/assignee/list`);
    }

    uploadFile(file: File): AxiosPromise<SystemFileDto> {
        let formData = new FormData();
        formData.append("file", file);

        return this.cookieAuthorizedApi.post<SystemFileDto>(this.filesUrl, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    }

    addAttachment(taskId: number, attachmentHash: string): AxiosPromise<TaskDto> {
        return this.cookieAuthorizedApi.post(`${this.dashboardUrl}/${taskId}/attachment`, { hash: attachmentHash });
    }

    removeAttachment(taskId: number, attachmentId: number): AxiosPromise<TaskDto> {
        return this.cookieAuthorizedApi.delete(`${this.dashboardUrl}/${taskId}/attachment/${attachmentId}`);
    }

    getFileToken(attachmentHash: string): AxiosPromise<TokenDto> {
        return this.cookieAuthorizedApi.get(`${this.filesUrl}/${attachmentHash}`);
    }

    retrieveFile(token: string): AxiosPromise<Blob> {
        return this.cookieAuthorizedApi.get(`${constants.filesDownloadUrl}/${token}`, { responseType: "blob" });
    }

    private formatDate(date: Date) {
        return format(date, constants.queryStringDateFormat);
    }
}

export class TasksServiceWithResponse implements TasksService {
    _response: any = {
        data: this.response,
    };

    constructor(private response: any) {}

    getTask(id: number) {
        return Promise.resolve(this._response);
    }

    getTasks(range: DateRange) {
        return Promise.resolve(this._response);
    }

    updateTaskStatus(updateTaskStatusData: UpdateTaskStatusData): AxiosPromise {
        return Promise.resolve(this._response);
    }

    updateTaskAttribute(taskId: number, attributeId: number, customValue: DictionaryItemDto): AxiosPromise {
        return Promise.resolve(this._response);
    }

    updateTaskAttributeMulti(taskId: number, attributeId: number, customValues: DictionaryItemDto[]): AxiosPromise {
        return Promise.resolve(this._response);
    }

    addComment(taskId: number, content: string): AxiosPromise {
        return Promise.resolve(this._response);
    }

    updateComment(taskId: number, commentId: number, content: string): AxiosPromise {
        return Promise.resolve(this._response);
    }

    deleteComment(taskId: number, commentId: number): AxiosPromise {
        return Promise.resolve(this._response);
    }

    updatePlannedDate(taskId: number, date: Date): AxiosPromise {
        return Promise.resolve(this._response);
    }

    assignUser(taskId: number, userName: string): AxiosPromise {
        return Promise.resolve(this._response);
    }

    getAvailableAssignees(taskId: number) {
        return Promise.resolve(this._response);
    }

    uploadFile(file: File): AxiosPromise<SystemFileDto> {
        return Promise.resolve(this._response);
    }

    addAttachment(taskId: number, attachmentHash: string): AxiosPromise<TaskDto> {
        return Promise.resolve(this._response);
    }

    removeAttachment(taskId: number, attachmentId: number): AxiosPromise<TaskDto> {
        return Promise.resolve(this._response);
    }

    getFileToken(attachmentHash: string): AxiosPromise<TokenDto> {
        return Promise.resolve(this._response);
    }

    retrieveFile(token: string): AxiosPromise<Blob> {
        return Promise.resolve(this._response);
    }
}

export class TasksServiceWithError implements TasksService {
    constructor(private error: any) {}

    getTask(id: number) {
        return Promise.reject(this.error);
    }

    getTasks(range: DateRange) {
        return Promise.reject(this.error);
    }

    updateTaskStatus(updateTaskStatusData: UpdateTaskStatusData): AxiosPromise {
        return Promise.reject(this.error);
    }

    updateTaskAttribute(taskId: number, attributeId: number, customValue: DictionaryItemDto): AxiosPromise {
        return Promise.reject(this.error);
    }

    updateTaskAttributeMulti(taskId: number, attributeId: number, customValues: DictionaryItemDto[]): AxiosPromise {
        return Promise.reject(this.error);
    }

    addComment(taskId: number, content: string): AxiosPromise {
        return Promise.reject(this.error);
    }

    updateComment(taskId: number, commentId: number, content: string): AxiosPromise {
        return Promise.reject(this.error);
    }

    deleteComment(taskId: number, commentId: number): AxiosPromise {
        return Promise.reject(this.error);
    }

    updatePlannedDate(taskId: number, date: Date): AxiosPromise {
        return Promise.reject(this.error);
    }

    assignUser(taskId: number, userName: string): AxiosPromise {
        return Promise.reject(this.error);
    }

    getAvailableAssignees(taskId: number) {
        return Promise.reject(this.error);
    }

    uploadFile(file: File): AxiosPromise<SystemFileDto> {
        return Promise.reject(this.error);
    }

    addAttachment(taskId: number, attachmentHash: string): AxiosPromise<TaskDto> {
        return Promise.reject(this.error);
    }

    removeAttachment(taskId: number, attachmentId: number): AxiosPromise<TaskDto> {
        return Promise.reject(this.error);
    }

    getFileToken(attachmentHash: string): AxiosPromise<TokenDto> {
        return Promise.reject(this.error);
    }

    retrieveFile(token: string): AxiosPromise<Blob> {
        return Promise.reject(this.error);
    }
}

export default new DefaultTasksService(
    new CookieAuthorizedApiWithAuthRefresh().GetInstance(() => {
        return refreshAuthMethodProvider.AuthorizeRefreshMethod();
    })
);
